import { VISUAL_NODE_TYPE } from "@dataspecer/visual-model";
import { addToRecordArray, createIdentifier, placePositionOnGrid } from "../../util/utils.js";
import { getEdgeSourceAndTargetRelationship } from "../../layout-algorithms/entity-bundles.js";
import { isEntityWithIdentifierAnchored } from "../../explicit-anchors.js";
/**
 * Represents visual node as in the cme visual model, but with couple of additional fields -
 * {@link width}, {@link height}, {@link isOutsider} and {@link isAnchored}, which is internal anchor,
 * because in some cases we want to anchor nodes which were not originally and anchored and also the other way around.
 * The {@link isOutsider} is used to mark nodes, which were NOT part of given visual model. We have this variable, because sometimes we want to layout nodes
 * which are not (yet) part of the visual model. In case when we didn't have any visual model on input, this property can be safely ignored. (It is always false.)
 */
export class VisualNodeComplete {
    coreVisualNode;
    width;
    height;
    isAnchored;
    isOutsider;
    constructor(coreVisualNode, width, height, useCopyOfCoreVisualNode, isOutsider, isAnchored) {
        if (useCopyOfCoreVisualNode) {
            this.coreVisualNode = { ...coreVisualNode };
        }
        else {
            this.coreVisualNode = coreVisualNode;
        }
        this.width = width;
        this.height = height;
        this.isOutsider = isOutsider;
        if (isAnchored === undefined) {
            this.isAnchored = coreVisualNode?.position?.anchored ?? false;
        }
        else {
            this.isAnchored = isAnchored;
        }
    }
    /**
     * Sets the position to given one, and puts the result on grid.
     */
    setPositionInCoreVisualNode(newX, newY) {
        this.coreVisualNode.position.x = newX;
        this.coreVisualNode.position.y = newY;
        placePositionOnGrid(this.coreVisualNode.position, 10, 10);
    }
    addToPositionInCoreVisualNode(x, y) {
        const newX = this.coreVisualNode.position.x + x;
        const newY = this.coreVisualNode.position.y + y;
        this.setPositionInCoreVisualNode(newX, newY);
    }
}
export class DefaultNode {
    constructor(mainGraph, visualNode, semanticEntityRepresentingNode, isProfile, sourceModelIdentifier, extractedModels, sourceGraph, explicititPosition, isDummy = false, explicitAnchors) {
        this.mainGraph = mainGraph;
        this.sourceModelIdentifier = sourceModelIdentifier;
        this.sourceGraph = sourceGraph;
        this.semanticEntityRepresentingNode = semanticEntityRepresentingNode;
        this.isProfile = isProfile;
        this.isDummy = isDummy;
        if (extractedModels === null) {
            return;
        }
        // We don't really need the whole thing.
        // We just need the attribute so storing the target of the relationship should be enough !
        // But we store it all for now.
        this.attributes = extractedModels.attributes.filter(attributesBundle => {
            const { source, target, ...rest } = getEdgeSourceAndTargetRelationship(attributesBundle.semanticRelationship);
            return this?.semanticEntityRepresentingNode?.id === source;
        }).map(attributeBundle => attributeBundle.semanticRelationship);
        if (visualNode !== null) {
            // Kind of ugly,
            // but the reason why need to do this is because the Reactflow dimension handlers, need the id.
            this.id = visualNode.id;
        }
        const width = this.mainGraph.nodeDimensionQueryHandler.getWidth(this);
        const height = this.mainGraph.nodeDimensionQueryHandler.getHeight(this);
        const isOutsider = visualNode === null;
        if (visualNode === null) {
            let isAnchored = false;
            if (explicitAnchors !== undefined) {
                isAnchored = isEntityWithIdentifierAnchored(semanticEntityRepresentingNode.id, explicitAnchors, false);
            }
            const coreVisualNode = DefaultNode.createNewVisualNodeBasedOnSemanticData(explicititPosition, this.semanticEntityRepresentingNode.id, this.sourceModelIdentifier);
            this.completeVisualNode = new VisualNodeComplete(coreVisualNode, width, height, false, isOutsider, isAnchored);
        }
        else {
            let isAnchored = visualNode.position.anchored ?? false;
            if (explicitAnchors !== undefined) {
                isAnchored = isEntityWithIdentifierAnchored(visualNode.id, explicitAnchors, isAnchored);
            }
            this.completeVisualNode = new VisualNodeComplete(visualNode, width, height, true, isOutsider, isAnchored);
            if (explicititPosition !== null) {
                this.completeVisualNode.coreVisualNode.position = {
                    ...explicititPosition,
                    anchored: this.completeVisualNode.coreVisualNode.position.anchored
                };
            }
        }
        this.id = this.completeVisualNode.coreVisualNode.id;
        sourceGraph.nodes[this.id] = this;
        mainGraph.insertInAllNodes(this);
        if (semanticEntityRepresentingNode === null) {
            addToRecordArray(this.id, this, this.mainGraph.semanticNodeToVisualMap);
        }
        else {
            addToRecordArray(semanticEntityRepresentingNode.id, this, this.mainGraph.semanticNodeToVisualMap);
        }
    }
    static createNewVisualNodeBasedOnSemanticData(position, semanticEntityRepresentingNodeIdentifier, sourceModelIdentifier) {
        if (position === null) {
            position = { x: 0, y: 0 };
        }
        return {
            id: createIdentifier(),
            type: [VISUAL_NODE_TYPE],
            representedEntity: semanticEntityRepresentingNodeIdentifier,
            position: {
                x: position.x,
                y: position.y,
                anchored: null,
            },
            content: [],
            visualModels: [],
            model: sourceModelIdentifier ?? "",
        };
    }
    getSourceGraph() {
        return this.sourceGraph;
    }
    setSourceGraph(sourceGraph) {
        this.sourceGraph = sourceGraph;
    }
    getAttributes() {
        return this.attributes;
    }
    convertToDataspecerRepresentation() {
        return this.completeVisualNode?.coreVisualNode ?? null;
    }
    mainGraph;
    sourceGraph;
    id;
    sourceModelIdentifier;
    semanticEntityRepresentingNode;
    isDummy;
    isProfile;
    completeVisualNode;
    attributes;
    isConsideredInLayout = true;
    layoutOptions = {};
    outgoingClassProfileEdges = [];
    incomingClassProfileEdges = [];
    outgoingProfileEdges = [];
    incomingProfileEdges = [];
    outgoingGeneralizationEdges = [];
    incomingGeneralizationEdges = [];
    outgoingRelationshipEdges = [];
    incomingRelationshipEdges = [];
    getAllIncomingEdges() {
        return getAllIncomingEdges(this);
    }
    getAllOutgoingEdges() {
        return getAllOutgoingEdges(this);
    }
    getAllEdges() {
        return getAllEdges(this);
    }
    getAllIncomingUniqueEdges() {
        return getAllIncomingUniqueEdges(this, null);
    }
    getAllOutgoingUniqueEdges() {
        return getAllOutgoingUniqueEdges(this, null);
    }
    getAllUniqueEdges() {
        return getAllUniqueEdges(this);
    }
}
/**
* @returns Returns generator which can be iterated to get edges of all types, where {@link node} is target/end.
*/
export function getAllIncomingEdges(node) {
    const internalGenerator = getEdgesInternal([node.incomingRelationshipEdges, node.incomingGeneralizationEdges,
        node.incomingProfileEdges, node.incomingClassProfileEdges]);
    return internalGenerator;
}
/**
* @returns Returns generator which can be iterated to get edges of all types, where {@link node} is source/start.
*/
export function getAllOutgoingEdges(node) {
    // Note: I couldn't find out, why can't I just somehow return the internals of the getEdgesInternal function
    // Answer: I just had to remove the * in front of method to say that it just returns the generator and isn't the generator in itself
    const internalGenerator = getEdgesInternal([node.outgoingRelationshipEdges, node.outgoingGeneralizationEdges,
        node.outgoingProfileEdges, node.outgoingClassProfileEdges]);
    return internalGenerator;
}
/**
* Internal method to create generator from the given edges of different types.
*/
function* getEdgesInternal(edgesOfDifferentTypes) {
    for (const edgesOfOneType of edgesOfDifferentTypes) {
        // Note: Can't use forEach because of yield
        for (const e of edgesOfOneType) {
            yield e;
        }
    }
}
/**
* @returns Returns generator which can be iterated to get edges, where {@link node} is either start or end.
*/
export function* getAllEdges(node) {
    const incomingEdges = node.getAllIncomingEdges();
    const outgoingEdges = node.getAllOutgoingEdges();
    yield* incomingEdges;
    yield* outgoingEdges;
}
/**
* @returns Returns generator which can be iterated to get edges of all types, where {@link node} is target/end.
* But only those which have unique end. That is we return only those edges, so the resulting subgraph is not a multi-graph
* (there is at most one edge between 2 nodes and no loops)
* After the generator is depleted, the map which maps visited nodes to true is returned
*/
export function getAllIncomingUniqueEdges(node, endsWhichAreAlreadyProcessed) {
    const internalGenerator = getEdgesUniqueInternal(node.id, [node.incomingRelationshipEdges, node.incomingGeneralizationEdges,
        node.incomingProfileEdges, node.incomingClassProfileEdges], endsWhichAreAlreadyProcessed);
    return internalGenerator;
}
/**
* @returns Returns generator which can be iterated to get edges of all types, where {@link node} is source/start.
* But only those which have unique end. That is we return only those edges, so the resulting subgraph is not a multi-graph
* (there is at most one edge between 2 nodes and no loops)
* After the generator is depleted, the map which maps visited nodes to true is returned
*/
export function getAllOutgoingUniqueEdges(node, endsWhichAreAlreadyProcessed) {
    // Note: I couldn't find out, why can't I just somehow return the internals of the getEdgesInternal function
    // Answer: I just had to remove the * in front of method to say that it just returns the generator and isn't the generator in itself
    const internalGenerator = getEdgesUniqueInternal(node.id, [node.outgoingRelationshipEdges, node.outgoingGeneralizationEdges,
        node.outgoingProfileEdges, node.outgoingClassProfileEdges], endsWhichAreAlreadyProcessed);
    return internalGenerator;
}
/**
* Internal method to create generator from the given edges of different types.
* Returning only those with unique ends.
* After the generator is depleted, the map which maps visited nodes to true is returned
* @param rootNode is the node which is contained in all of the edges
*
*/
function* getEdgesUniqueInternal(rootNode, edgesOfDifferentTypes, endsWhichAreAlreadyProcessed) {
    const noLongerUniqueEnds = {
        [rootNode]: true,
        ...(endsWhichAreAlreadyProcessed ?? {})
    };
    for (const edgesOfOneType of edgesOfDifferentTypes) {
        // Note: Can't use forEach because of yield
        for (const e of edgesOfOneType) {
            if (noLongerUniqueEnds[e.start.id] !== undefined && noLongerUniqueEnds[e.end.id] !== undefined) {
                continue;
            }
            yield e;
            noLongerUniqueEnds[e.start.id] = true;
            noLongerUniqueEnds[e.end.id] = true;
        }
    }
    return noLongerUniqueEnds;
}
/**
* @returns Returns generator which can be iterated to get edges, where {@link node} is either start or end.
* But only those which have unique end. That is we return only those edges, so the resulting subgraph is not a multi-graph
* (there is at most one edge between 2 nodes and no loops).
* After the generator is depleted, the map which maps visited nodes to true is returned
*/
export function* getAllUniqueEdges(node) {
    const incomingEdges = getAllIncomingUniqueEdges(node, null);
    yield* incomingEdges;
    let noLongerUniqueEnds = incomingEdges.next();
    while (!noLongerUniqueEnds.done) {
        noLongerUniqueEnds = incomingEdges.next();
    }
    const outgoingEdges = getAllOutgoingUniqueEdges(node, noLongerUniqueEnds.value);
    yield* outgoingEdges;
    noLongerUniqueEnds = outgoingEdges.next();
    while (!noLongerUniqueEnds.done) {
        noLongerUniqueEnds = outgoingEdges.next();
    }
    return noLongerUniqueEnds.value;
}
/**
 * @returns Returns true if the node is inside the visual model or
 * if the model is null or if the given {@link classIdentifier} is inside {@link outsiders}.
 */
export const isNodeInVisualModel = (visualModel, entitiesToLayout, node, classIdentifier) => {
    if (visualModel === null) {
        return classIdentifier != null && entitiesToLayout.outsiders[classIdentifier] !== undefined;
    }
    if (node !== null) {
        return true;
    }
    if (classIdentifier === undefined || classIdentifier === null) {
        return false;
    }
    const visualEntities = visualModel.getVisualEntitiesForRepresented(classIdentifier);
    let isPresentInVisualEntitiesToLayout = false;
    for (const visualEntity of visualEntities) {
        isPresentInVisualEntitiesToLayout = isPresentInVisualEntitiesToLayout || entitiesToLayout.visualEntities.includes(visualEntity.id);
    }
    const isPresentInVisualModel = isPresentInVisualEntitiesToLayout ||
        entitiesToLayout.outsiders[classIdentifier] !== undefined;
    return isPresentInVisualModel;
};
export function addNodeToGraph(mainGraph, node, semanticEntityRepresentingNode, isProfile, sourceModelIdentifier, extractedModels, sourceGraph, visualModel, entitiesToLayout, explicititPosition, isDummy = false, explicitAnchors) {
    if (isNodeInVisualModel(visualModel, entitiesToLayout, node, semanticEntityRepresentingNode?.id)) {
        new DefaultNode(mainGraph, node, semanticEntityRepresentingNode, isProfile, sourceModelIdentifier, extractedModels, sourceGraph, explicititPosition, isDummy, explicitAnchors);
        return true;
    }
    return false;
}
//# sourceMappingURL=node.js.map